import axios from 'axios'

/**
 * Ponto único de verdade sobre a sessão do usuário (token Passport + refresh).
 *
 * Antes desta camada, token e dados do usuário eram gravados e limpos em quatro
 * lugares diferentes (Login.vue, Header.vue, mixins/tokenInfo, mixins/trataResponseErro),
 * e nada renovava o token — passada a validade, toda requisição caía em 401 e o
 * backend registrava "The resource owner or authorization server denied the request".
 */

// Instância própria, sem os interceptors do axiosLaravel: a chamada de refresh
// nunca deve disparar outra tentativa de refresh.
const http = axios.create({
  baseURL: process.env.BASEURLLARAVEL,
  headers: {'Content-Type': 'application/json;charset=UTF-8'}
})

// Renovação em andamento. Várias requisições que recebem 401 ao mesmo tempo
// compartilham esta Promise — sem isso, refreshes concorrentes se invalidariam
// em cadeia (o backend trata reapresentação como vazamento) e derrubariam o usuário.
let renovacaoEmAndamento = null

/** Margem antes do exp em que já vale a pena renovar, evitando o 401. */
const MARGEM_RENOVACAO_SEGUNDOS = 120

/*
 * Lock entre abas. O refresh token é de uso único e rotativo: se duas abas o
 * apresentarem, a segunda é tratada pelo backend como vazamento e derruba TODAS
 * as sessões do usuário. Como o SGP abre abas novas (ver mixins/laravelHandoff),
 * isso aconteceria no uso normal. A aba que assume o lock renova; as outras
 * esperam o novo token aparecer no localStorage, que é compartilhado na origem.
 */
const CHAVE_LOCK = 'refreshTokenLaravelLock'
const LOCK_MS = 15000
const ESPERA_MS = 150

export function salvaSessao (data) {
  if (!window.localStorage) {
    return
  }

  window.localStorage.setItem('tokenLaravel', data.access_token)
  window.localStorage.setItem('tokenLaravelExpires_at', data.expires_at)

  if (data.refresh_token) {
    window.localStorage.setItem('refreshTokenLaravel', data.refresh_token)
  }

  if (data.usuario) {
    window.localStorage.setItem('usuario', JSON.stringify(data.usuario))
    window.localStorage.setItem('userPermissions', JSON.stringify(data.usuario.permissoes))
  }

  aplicaToken(data.access_token)
}

export function limpaSessao () {
  if (window.localStorage) {
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('user')
    window.localStorage.removeItem('tokenLaravel')
    window.localStorage.removeItem('refreshTokenLaravel')
    window.localStorage.removeItem('tokenLaravelExpires_at')
    window.localStorage.removeItem('usuario')
    window.localStorage.removeItem('userPermissions')
    window.localStorage.removeItem(CHAVE_LOCK)
  }

  renovacaoEmAndamento = null

  if (window.Echo) {
    try {
      window.Echo.disconnect()
    } catch (e) {
      // Desconectar um socket já morto não deve impedir a limpeza da sessão.
    }
  }
}

export function getAccessToken () {
  return window.localStorage ? window.localStorage.getItem('tokenLaravel') : null
}

export function getRefreshToken () {
  return window.localStorage ? window.localStorage.getItem('refreshTokenLaravel') : null
}

/** Segundos restantes do access token atual, lidos do `exp` do JWT. */
export function segundosAteExpirar () {
  let token = getAccessToken()

  if (!token) {
    return 0
  }

  try {
    let base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    let exp = JSON.parse(window.atob(base64)).exp

    return exp - Math.floor(new Date().getTime() / 1000)
  } catch (e) {
    return 0
  }
}

export function precisaRenovar () {
  return !!getRefreshToken() && segundosAteExpirar() < MARGEM_RENOVACAO_SEGUNDOS
}

/**
 * Troca o refresh token por um novo par. Chamadas simultâneas compartilham a
 * mesma requisição. Rejeita quando não há refresh token ou o backend recusa —
 * cabe a quem chamou limpar a sessão e mandar para o login.
 */
export function renovaToken () {
  if (renovacaoEmAndamento) {
    return renovacaoEmAndamento
  }

  let refreshToken = getRefreshToken()

  if (!refreshToken) {
    return Promise.reject(new Error('sem refresh token'))
  }

  let tokenAnterior = getAccessToken()

  if (!assumeLock()) {
    renovacaoEmAndamento = aguardaOutraAba(tokenAnterior)

    return renovacaoEmAndamento
  }

  // Sem `.finally` de propósito: o build alvo (Vue 2 + babel-polyfill) não
  // garante Promise.prototype.finally.
  renovacaoEmAndamento = http.post('auth/refresh', {refresh_token: refreshToken})
    .then((response) => {
      liberaLock()
      renovacaoEmAndamento = null
      salvaSessao(response.data)
      reconectaWebsocket(response.data.access_token)

      return response.data.access_token
    })
    .catch((error) => {
      liberaLock()
      renovacaoEmAndamento = null

      return Promise.reject(error)
    })

  return renovacaoEmAndamento
}

/**
 * Tenta assumir o lock de renovação. O valor carrega um nonce para que duas abas
 * que escrevam no mesmo milissegundo não se considerem ambas vencedoras.
 */
function assumeLock () {
  if (!window.localStorage) {
    return true
  }

  let agora = new Date().getTime()
  let atual = window.localStorage.getItem(CHAVE_LOCK)

  if (atual && agora - parseInt(atual.split(':')[0], 10) < LOCK_MS) {
    return false
  }

  let meu = agora + ':' + Math.random().toString(36).slice(2)
  window.localStorage.setItem(CHAVE_LOCK, meu)

  return window.localStorage.getItem(CHAVE_LOCK) === meu
}

function liberaLock () {
  if (window.localStorage) {
    window.localStorage.removeItem(CHAVE_LOCK)
  }
}

/** Espera a aba que detém o lock gravar o novo access token no localStorage. */
function aguardaOutraAba (tokenAnterior) {
  return new Promise((resolve, reject) => {
    let limite = new Date().getTime() + LOCK_MS

    let verifica = function () {
      let atual = getAccessToken()

      if (atual && atual !== tokenAnterior) {
        renovacaoEmAndamento = null
        aplicaToken(atual)
        resolve(atual)

        return
      }

      if (new Date().getTime() > limite) {
        renovacaoEmAndamento = null
        reject(new Error('tempo esgotado esperando a renovação de outra aba'))

        return
      }

      window.setTimeout(verifica, ESPERA_MS)
    }

    verifica()
  })
}

function aplicaToken (token) {
  if (window.axiosLaravel) {
    window.axiosLaravel.defaults.headers.Authorization = 'Bearer ' + token
  }
}

/**
 * O Echo recebe o token uma única vez, na conexão. Depois de uma rotação, o
 * endpoint `broadcasting/auth` passaria a recusar a inscrição em novos canais.
 */
function reconectaWebsocket (token) {
  if (!window.Echo || !window.Echo.connector || !window.Echo.connector.options) {
    return
  }

  window.Echo.connector.options.auth = window.Echo.connector.options.auth || {}
  window.Echo.connector.options.auth.headers = Object.assign(
    {},
    window.Echo.connector.options.auth.headers,
    {Authorization: 'Bearer ' + token}
  )

  if (window.Echo.connector.pusher && window.Echo.connector.pusher.config) {
    window.Echo.connector.pusher.config.auth = window.Echo.connector.options.auth
  }
}

/**
 * Instala os interceptors de renovação na instância informada.
 *
 * - request: renova antes de enviar quando o token está a menos de 2 minutos de expirar.
 * - response: em 401, renova uma vez e reenvia a requisição original.
 */
export function instalaInterceptors (instancia, aoPerderSessao) {
  instancia.interceptors.request.use((config) => {
    // O header default desta aba só muda quando ela mesma renova. Se outra aba
    // rotacionou, o token em memória já foi revogado pelo backend (o refresh
    // revoga o access token anterior) — o valor atual está no localStorage.
    let atual = getAccessToken()

    if (atual) {
      config.headers.Authorization = 'Bearer ' + atual
    }

    if (config._retry || !precisaRenovar()) {
      return config
    }

    return renovaToken()
      .then((token) => {
        config.headers.Authorization = 'Bearer ' + token

        return config
      })
      .catch(() => config)
  })

  instancia.interceptors.response.use(
    (response) => response,
    (error) => {
      let config = error.config

      if (!error.response || error.response.status !== 401 || !config) {
        return Promise.reject(error)
      }

      // 401 mesmo depois de renovar: a sessão acabou de fato. Sem isso a tela
      // ficava aberta acumulando avisos de sessão expirada, sem ir ao login.
      if (config._retry) {
        aoPerderSessao()

        return Promise.reject(error)
      }

      config._retry = true

      // Outra aba renovou enquanto esta requisição estava no ar: basta reenviar
      // com o token novo. Um refresh aqui revogaria o token das demais abas e
      // elas, por sua vez, revogariam o desta, em cadeia.
      let atual = getAccessToken()
      let usado = String(config.headers.Authorization || '').replace('Bearer ', '')

      if (atual && atual !== usado) {
        aplicaToken(atual)
        config.headers.Authorization = 'Bearer ' + atual

        return instancia.request(config)
      }

      if (!getRefreshToken()) {
        aoPerderSessao()

        return Promise.reject(error)
      }

      return renovaToken()
        .then((token) => {
          config.headers.Authorization = 'Bearer ' + token

          return instancia.request(config)
        })
        .catch(() => {
          aoPerderSessao()

          return Promise.reject(error)
        })
    }
  )
}



// WEBPACK FOOTER //
// ./src/auth.js